import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Injectable,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { CustomNavbarComponent } from '@app/finxone-web-frontend/app/Modules/layouts/custom-navbar/custom-navbar.component';
import { WorkflowFormGuard } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-form.guard';
import { redirectToPageWithUrl } from '@app/finxone-web-frontend/app/lib/utils/navbar-navigation.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { NavBarMobile, baseCssConfigStyle, defaultNavBar } from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { KeycloakAngularModule } from 'keycloak-angular';
import { SharedModule } from 'primeng/api';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { RemoveAllProgressBarStack } from '../../../lib/actions/progress-bar.action';
import { ConfigService } from '../../../lib/services/config-service/config-service.service';
import { RoleState, RoleStateModel } from '../../../lib/state/role.state';

/**
 * WARNING Put this in it's own service file
 */
/**
 * Used by the device holder compoennt in the workflow frontend to track translate y postion for the navbars
 * inside the cdk scroll viewports
 */
@Injectable({ providedIn: 'root' })
export class ScrollViewportService {
  private scrollOffsetSubject = new BehaviorSubject<number>(0);
  private viewportHeightSubject = new BehaviorSubject<number>(0);

  scrollOffset$: Observable<number> = this.scrollOffsetSubject.asObservable();
  viewportHeight$: Observable<number> = this.viewportHeightSubject.asObservable();

  updateScrollPosition(scrollTop: number, viewportHeight: number): void {
    this.scrollOffsetSubject.next(scrollTop);
    this.viewportHeightSubject.next(viewportHeight);
  }
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-footer-navbar',
  templateUrl: './footer-navbar.component.html',
  styleUrls: ['./footer-navbar.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    RouterModule,
    KeycloakAngularModule,
    MatIconModule,
    CustomNavbarComponent,
  ],
})
export class FooterNavbarComponent implements OnInit, OnChanges, AfterViewInit {
  navbarPosition = 0;

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @ViewChild('navbar', { static: false }) navbar!: ElementRef<HTMLElement>;
  @ViewChild('contentContainer', { static: false }) customNavbar!: ElementRef<HTMLElement>;
  @Output() footerNavbarHeightChange = new EventEmitter<number>();
  @Input() footerStyles: baseCssConfigStyle;
  @Input() currentUrl = '';
  hideChart = false;
  hideProfile = false;
  @Select(RoleState.getRole)
  role!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  navBarMobileConfig: NavBarMobile;
  public containerStyles: baseCssConfigStyle;
  public bottomPadding = '0px';
  public htmlContent = '';

  constructor(
    private router: Router,
    private configService: ConfigService,
    private store: Store,
    private injector: Injector,
    private scrollService: ScrollViewportService,
  ) {
    this.updateSafeAreaPadding();

    this.configService.getConfig().subscribe(() => {
      this.updateSafeAreaPadding();
    });

    this.role.subscribe((role) => {
      this.roleInfo = role;
      this.getNavBarUIFromSystemConfig();
    });

    const source$ = fromEvent<StorageEvent>(window, 'storage');

    source$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.key === 'APP_PREFERENCES') {
        this.updateSafeAreaPadding();
      }
    });
  }

  private updateSafeAreaPadding() {
    // This is the in app padding needed for the safe area on mobile devices
    this.configService
      .getSafePadding()
      .pipe(take(1))
      .subscribe((padding) => {
        if (padding.isIOS) {
          this.bottomPadding = `${padding.bottomPadding}px`;
        } else {
          this.bottomPadding = '0px';
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateSafeAreaPadding();
  }

  ngOnInit() {
    if (this.currentUrl === '') {
      this.router.events
        .pipe(
          takeUntil(this.destroy$),
          map(() => {
            this.currentUrl = this.router.url;
          }),
        )
        .subscribe();
    }
    this.getNavBarUIFromSystemConfig();

    // just periodically check if padding should be updated every 500ms
    timer(0, 500)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateSafeAreaPadding();
      });

    combineLatest([this.scrollService.scrollOffset$, this.scrollService.viewportHeight$])
      .pipe(distinctUntilChanged(), debounceTime(100), takeUntil(this.destroy$))
      .subscribe(([scrollTop, viewportHeight]) => {
        this.navbarPosition = scrollTop;
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateNavbarHeight();
    });
  }

  routeContains(route: string) {
    return this.router.url.includes(route);
  }

  public navigateToZone(zoneUrl: string): void {
    // Disable form validation check for navigating to the set page
    WorkflowFormGuard.disableFormValidation();
    this.store.dispatch(new RemoveAllProgressBarStack());
    redirectToPageWithUrl(zoneUrl, this.injector);
  }

  getNavBarUIFromSystemConfig() {
    this.configService
      .getWidgetsConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        const navBarConfig = config?.navbar[this.roleInfo.role]?.navBarMobile;

        if (navBarConfig) {
          this.navBarMobileConfig = navBarConfig;
        } else {
          this.navBarMobileConfig = defaultNavBar.navBarMobile;
        }

        if (this.navBarMobileConfig.useHtml) {
          this.htmlContent = this.navBarMobileConfig.htmlContent;
        } else {
          this.footerStyles = {
            ...this.footerStyles,
            'background-color': this.navBarMobileConfig.navBarColor,
            'border-width': this.navBarMobileConfig.borderWidth.toString() + 'px',
            'border-color': this.navBarMobileConfig.borderColor,
            'border-style': 'solid',
            'border-bottom-left-radius':
              this.navBarMobileConfig.borderRadiusBottomLeft.toString() +
              this.navBarMobileConfig.borderRadiusUnit,
            'border-bottom-right-radius':
              this.navBarMobileConfig.borderRadiusBottomRight.toString() +
              this.navBarMobileConfig.borderRadiusUnit,
            'border-top-left-radius':
              this.navBarMobileConfig.borderRadiusTopLeft.toString() +
              this.navBarMobileConfig.borderRadiusUnit,
            'border-top-right-radius':
              this.navBarMobileConfig.borderRadiusTopRight.toString() +
              this.navBarMobileConfig.borderRadiusUnit,
          };
          /**
           * added nullable, in some case padding data does not come.
           */
          this.containerStyles = {
            ...this.containerStyles,
            'padding-left':
              this.navBarMobileConfig.paddingLeft?.toString() + this.navBarMobileConfig?.paddingUnit,
            'padding-right':
              this.navBarMobileConfig.paddingRight?.toString() + this.navBarMobileConfig?.paddingUnit,
            'padding-top':
              this.navBarMobileConfig.paddingTop.toString() + this.navBarMobileConfig?.paddingUnit,
            'padding-bottom':
              this.navBarMobileConfig.paddingBottom?.toString() + this.navBarMobileConfig?.paddingUnit,
            'margin-top': this.navBarMobileConfig.marginTop?.toString() + this.navBarMobileConfig?.marginUnit,
            'margin-bottom':
              this.navBarMobileConfig.marginBottom?.toString() + this.navBarMobileConfig?.marginUnit,
            'margin-left':
              this.navBarMobileConfig.marginLeft?.toString() + this.navBarMobileConfig?.marginUnit,
            'margin-right':
              this.navBarMobileConfig.marginRight?.toString() + this.navBarMobileConfig?.marginUnit,
          };
        }
      });
  }

  /**
   * Measures the height of the footer navbar (standard or custom) and emits it
   * so the parent can adjust the main content height to avoid overlap.
   */
  public calculateNavbarHeight() {
    if (this.navbar && this.navbar.nativeElement) {
      const footerNavHeight = this.navbar.nativeElement.offsetHeight;
      this.footerNavbarHeightChange.emit(footerNavHeight);
    }
    if (this.customNavbar && this.customNavbar.nativeElement) {
      const footerNavHeight = this.customNavbar.nativeElement.offsetHeight;
      this.footerNavbarHeightChange.emit(footerNavHeight);
    }
  }
}
